/**
 * Kinde Manager Hook - React Integration for Kinde SDK
 *
 * Provides React hook interface that matches useMsalManager patterns.
 * This allows components to use either MSAL or Kinde through a consistent API.
 *
 * @module useKindeAuth
 */

import { useCallback, useMemo } from 'react'
import { useKindeAuth as useKindeAuthSDK } from '@kinde-oss/kinde-auth-react'

/**
 * Kinde manager hook providing useMsalManager-compatible interface.
 *
 * Features:
 * - Matches useMsalManager API for seamless migration
 * - Maps Kinde user to MSAL-like account structure
 * - Exposes Kinde-specific features (permissions, registration)
 *
 * @returns {Object} Kinde management functions and state with properties:
 *   - ensureInitialized: Function - No-op (Kinde auto-initializes via KindeProvider)
 *   - getActiveAccount: Function - Gets currently active user in MSAL account format
 *   - getAllAccounts: Function - Gets all accounts (single account for Kinde)
 *   - setActiveAccount: Function - No-op (Kinde uses single session)
 *   - inProgress: string - Current interaction status ('login' or 'none')
 *   - isInitialized: boolean - Whether Kinde is initialized
 *   - isInitializing: boolean - Whether initialization is in progress
 *   - isInteractionInProgress: Function - Checks if interaction is in progress
 *   - kindeInstance: Object - Raw useKindeAuth() result for direct access
 *   - isAuthenticated: boolean - Whether user is currently authenticated
 *   - user: Object|null - Raw Kinde user object
 *   - login: Function - Initiates login flow
 *   - logout: Function - Initiates logout flow
 *   - register: Function - Initiates registration flow
 *   - getAccessToken: Function - Gets access token (async)
 *   - getPermission: Function - Checks single permission (async)
 */
export const useKindeManager = () => {
    const kindeAuth = useKindeAuthSDK()
    const { getAccessToken, getPermission, isAuthenticated, isLoading, login, logout, register, user } = kindeAuth

    /**
     * Ensures Kinde is initialized.
     * No-op since Kinde auto-initializes via KindeProvider.
     * Provided for useMsalManager compatibility.
     *
     * @returns {Promise<void>}
     */
    const ensureInitialized = useCallback(async () => {
        // Kinde initializes automatically via KindeProvider
        // This is a no-op for useMsalManager compatibility
        return Promise.resolve()
    }, [])

    /**
     * Maps Kinde user to MSAL-like account structure.
     * This allows components expecting MSAL account format to work with Kinde.
     *
     * @returns {Object|null} Account in MSAL-like format or null if not authenticated
     */
    const mapUserToAccount = useMemo(() => {
        if (!user) return null

        return {
            // MSAL account properties
            localAccountId: user.id,
            username: user.email || user.preferredEmail,
            name: [user.givenName, user.familyName].filter(Boolean).join(' ') || undefined,
            // MSAL idTokenClaims structure for compatibility
            idTokenClaims: {
                given_name: user.givenName,
                family_name: user.familyName,
                emails: [user.email || user.preferredEmail].filter(Boolean),
            },
        }
    }, [user])

    /**
     * Gets the currently active user account in MSAL-like format.
     *
     * @returns {Promise<Object|null>} Active account or null if not authenticated
     */
    const getActiveAccount = useCallback(async () => {
        return Promise.resolve(mapUserToAccount)
    }, [mapUserToAccount])

    /**
     * Gets all accounts.
     * Kinde uses single session, so this returns array with current account or empty array.
     *
     * @returns {Promise<Array>} Array with current account or empty array
     */
    const getAllAccounts = useCallback(async () => {
        return mapUserToAccount ? [mapUserToAccount] : []
    }, [mapUserToAccount])

    /**
     * Sets the active account.
     * No-op for Kinde since it uses single session model.
     * Provided for useMsalManager compatibility.
     *
     * @returns {Promise<void>}
     */
    const setActiveAccount = useCallback(async () => {
        // Kinde uses single session model - no account switching
        return Promise.resolve()
    }, [])

    /**
     * Checks if an interaction is currently in progress.
     *
     * @returns {boolean} Whether an interaction is in progress
     */
    const isInteractionInProgress = useCallback(() => {
        return isLoading
    }, [isLoading])

    /**
     * Maps Kinde isLoading to MSAL-like inProgress status.
     */
    const inProgress = isLoading ? 'login' : 'none'

    return {
        // useMsalManager-compatible interface
        ensureInitialized,
        getAllAccounts,
        getActiveAccount,
        inProgress,
        isInitialized: !isLoading,
        isInitializing: isLoading,
        isInteractionInProgress,
        setActiveAccount,

        // Kinde-specific (exposed for unified hook and direct access)
        kindeInstance: kindeAuth,
        isAuthenticated,
        user,
        login,
        logout,
        register,
        getAccessToken,
        getPermission,
    }
}
